import React from 'react';

import { CheckoutHandoffSection } from '@/booking-checkout/components';
import ResponsiveConditional from '@/common/components/responsive/ResponsiveConditional.component';
import LargeLoadingIndicator from '@/core/components/controls/LargeLoadingIndicator.component';
import { useFeatureSwitchStatus } from '@/features';
import { useHotel } from '@/hotel';
import { useTranslation } from '@/i18n';
import { CardFlushDivider, PageWrapper } from '@/ui/layout';
import { Spacer, Stack } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { Heading } from '@/ui/typography';

import { PaymentAndCancellationPolicyAnchor } from '../components/PaymentCancellationPolicyAnchor.component';
import { CheckoutHalfScreenContainer } from '../components/checkout/CheckoutHalfScreenLayout.component';
import StepProgress from '../components/wizard/StepProgress.component';
import { useBookingSummary } from '../hooks';
import { AdditionalInfoFormContainer } from './AdditionalInfoForm.container';
import { YourStaySummaryContainer } from './YourStaySummary.container';

interface BookingAdditionalInfoStepProps {
  bookingId: string;
  hotelSlug: string;
  onNextClick: (hasRequestedOneClickCheckout: boolean) => void;
  currentStepNumber: number;
  totalStepCount: number;
}

export const BookingAdditionalInfoStepContainer = ({
  bookingId,
  hotelSlug,
  onNextClick,
  currentStepNumber,
  totalStepCount,
}: BookingAdditionalInfoStepProps) => {
  const { isLoading } = useBookingSummary({
    bookingId,
    hotelSlug,
  });
  const hotel = useHotel(hotelSlug);

  const handoffEnabled = useFeatureSwitchStatus('mobile-handoff');

  const { t } = useTranslation('additionalInformation');

  const {
    componentProperties: { page },
  } = useTheme();

  if (isLoading) {
    return <LargeLoadingIndicator maxHeight={100} />;
  }

  if (!hotel) {
    return null;
  }

  return (
    <PageWrapper>
      <ResponsiveConditional
        desktop={
          <CheckoutHalfScreenContainer
            title={t('title')}
            bookingId={bookingId}
            hotelSlug={hotelSlug}
          >
            <Stack s="xl">
              <AdditionalInfoFormContainer
                bookingId={bookingId}
                hotelSlug={hotelSlug}
                onAfterSubmit={onNextClick}
              />
              {handoffEnabled && (
                <CheckoutHandoffSection
                  hotelSlug={hotelSlug}
                  bookingId={bookingId}
                />
              )}
            </Stack>
          </CheckoutHalfScreenContainer>
        }
        mobile={
          <>
            {/* Again - we're repeating this here. Need to extract into it's own component. */}
            <div>
              <Spacer s="m" />
              <StepProgress
                total={totalStepCount}
                current={currentStepNumber}
              />
              <Spacer s="m" />
              <Heading style="heading5" as="h5" css={page?.title?.mobile}>
                {t('title')}
              </Heading>
              <Spacer s="m" />

              <YourStaySummaryContainer
                bookingId={bookingId}
                hotelSlug={hotelSlug}
              />
              <Spacer s="s" />
              <PaymentAndCancellationPolicyAnchor
                hotelReferenceId={hotelSlug}
              />
              <Spacer s="xl" />
              <CardFlushDivider />
              <Spacer s="l" />
              <AdditionalInfoFormContainer
                bookingId={bookingId}
                hotelSlug={hotelSlug}
                onAfterSubmit={onNextClick}
              />
            </div>
          </>
        }
      />
    </PageWrapper>
  );
};
